import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { BRAND, NAV } from "@/constants/brand";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-40 bg-white" data-testid="site-header">
      {/* Top utility bar */}
      <div className="hc-banner-gradient text-white text-xs md:text-sm">
        <div className="hc-container py-2 flex items-center justify-between gap-4">
          <div className="hidden sm:flex items-center gap-4">
            <a href={`tel:${BRAND.phone}`} className="flex items-center gap-1.5 hover:opacity-90">
              <Phone className="w-3.5 h-3.5" /> {BRAND.phone}
            </a>
            <a href={`mailto:${BRAND.email}`} className="flex items-center gap-1.5 hover:opacity-90">
              <Mail className="w-3.5 h-3.5" /> {BRAND.email}
            </a>
          </div>
          <div className="text-white/90 truncate">
            Operated by {BRAND.parent} — Pilot operations in Asansol, Durgapur &amp; Bardhaman
          </div>
        </div>
      </div>

      <div className={`transition-shadow ${scrolled ? "shadow-md" : "shadow-sm"} bg-white`}>
        <div className="hc-container flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={BRAND.logoUrl} alt="HomeCure logo" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
            <div>
              <div className="text-[#3780E0] font-extrabold text-xl md:text-2xl leading-none tracking-tight">HomeCure</div>
              <div className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wider">by {BRAND.parent}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive ? "text-[#3780E0] bg-[#E8F0FB]" : "text-slate-700 hover:text-[#3780E0] hover:bg-slate-50"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <Link to="/partner" className="hc-btn-primary ml-2 text-sm py-2 px-4" data-testid="nav-partner-cta">
              Partner with us
            </Link>
          </nav>

          <button
            className="lg:hidden p-2 rounded-md hover:bg-slate-100"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6 text-[#3780E0]" /> : <Menu className="w-6 h-6 text-[#3780E0]" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-100" data-testid="mobile-menu">
            <div className="hc-container py-3 flex flex-col gap-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md text-sm font-semibold ${
                      isActive ? "text-[#3780E0] bg-[#E8F0FB]" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link to="/partner" className="hc-btn-primary mt-2 justify-center">
                Partner with us
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
